<template>
  <div
    v-if="messageType.isText"
    :class="{
      message: true,
      messageSingleLine: !multipleLines,
      messageMultipleLines: multipleLines
    }"
    :data-sender="sender"
  >
    {{ previewContent }}
  </div>

  <p
    v-else-if="messageType.isMedia"
    class="message messageType"
    :data-sender="sender"
  >
    <WIcon :icon="messageType.icon" size="medium" />
    {{ messageType.label }}
  </p>

  <p v-else class="message messageType">Canal sem mensagens</p>
</template>

<script>
import { WIcon } from '@alice-health/wonderland-vue'
import unescape from 'lodash/unescape'
import { mapGetters, mapState } from 'vuex'

import { MessageTypes } from '@/models/channels'

export default {
  components: {
    WIcon
  },
  props: {
    staffs: { type: Object, default: undefined },
    previewableMessages: { type: Array, default: undefined },
    lastPreviewableMessage: { type: Object, default: undefined },
    hideMemberName: Boolean,
    multipleLines: Boolean
  },
  data() {
    return {
      messageTypes: {
        [MessageTypes.AUDIO]: {
          label: 'Áudio',
          icon: 'icVolumeOn'
        },
        [MessageTypes.DOCUMENT]: {
          label: 'Documento',
          icon: 'icPaper'
        },
        [MessageTypes.IMAGE]: {
          label: 'Imagem',
          icon: 'icCamera'
        },
        [MessageTypes.VIDEO]: {
          label: 'Vídeo',
          icon: 'icVideoOn'
        }
      },
      typesWithContent: [
        MessageTypes.TEXT,
        MessageTypes.ANSWER,
        MessageTypes.QUESTION
      ]
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters('member', ['getMemberById']),
    type() {
      return this.lastPreviewableMessage?.type
    },
    isText() {
      return (
        this.previewableMessage || this.hasContent(this.lastPreviewableMessage)
      )
    },
    messageType() {
      const mediaMessage = this.messageTypes[this.type]

      return {
        isText: this.isText,
        isMedia: !!mediaMessage,
        ...(mediaMessage || {})
      }
    },
    lastMessageByCurrentUser() {
      return this.lastPreviewableMessage?.userId === this.user.id
    },
    member() {
      const channelPersonId = this.lastPreviewableMessage?.userId

      if (channelPersonId) {
        return this.getMemberById({ channelPersonId })
      }

      return null
    },
    hideSender() {
      return (
        !this.lastPreviewableMessage || (this.member && this.hideMemberName)
      )
    },
    sender() {
      if (this.hideSender) {
        return null
      }

      if (this.lastMessageByCurrentUser) return 'Você:'

      const { userId } = this.lastPreviewableMessage
      if (!userId) return 'Alice:'

      const sender = this.member || this.staffs[userId]
      if (!sender) return null
      const name = sender?.nickName || sender?.firstName || sender?.name
      return `${name}:`
    },
    previewableMessage() {
      return this.previewableMessages
        ?.filter(message => this.hasContent(message))
        .map(message => message.content)
        .join('. ')
    },
    previewContent() {
      return this.removeHtmlTags(
        this.previewableMessage || this.lastPreviewableMessage.content
      )
    }
  },
  methods: {
    hasContent(message) {
      return (
        message &&
        message.content &&
        this.typesWithContent.includes(message.type)
      )
    },
    removeHtmlTags(message) {
      return unescape(message.replace(/<\/?[^>]+(>|$)/g, ' '))
    }
  }
}
</script>

<style lang="scss" scoped>
.message {
  &[data-sender]::before {
    content: attr(data-sender);
  }

  [placeholder] & {
    height: 16px;
    background: var(--gl-color-shades-gray-20);
    border-radius: 4px;
    font-size: 0;
    width: 100%;
  }
}

.messageSingleLine {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.messageMultipleLines {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.messageType {
  display: flex;
  gap: 8px;
  align-items: center;
  color: var(--gl-color-shades-gray-90);
  fill: var(--gl-color-shades-gray-90);
  margin-bottom: 0;
}
</style>
